import * as React from "react"
import { connect } from "react-redux"
import { Modal, Button, Message, Icon, Divider } from "semantic-ui-react"
import {
  getActiveLang,
  getTranslationsByScopes,
  DropitState,
  getUserRole,
  getUserSelectedSupplier,
} from "../../core/reducers"
import CommonFields from "./form-field-containers/com_common-fields"
import { toastr } from "react-redux-toastr"
import {
  checkFieldValidity,
  FieldType,
  formatGetProductResponse,
  IHandleChangeProps,
  IRawSupplierReference,
  IReferenceIds,
  ISupplierInfo,
  ISupplierReference,
  ITranslations,
  mergeSelectedFullProducts,
  permanentFormIds,
} from "../../models"
import {
  IDropdownOption,
  IFieldState,
} from "../product-form/_reducers/_utils/product-form-types"
import { ProductFormState } from "../product-form/_reducers"
import { getActions, ProductFormActions } from "../product-form/_actions"
import {
  $getFieldsByFamilyTech,
  $getMultiModifProductList,
  $sendMultiModifInformations,
} from "../../core/requests"
import { createFormFieldByCaratech } from "../product-form/_reducers/_utils/create-field"
import { stringify, stringifyTree } from "../../utils/stringifyier"
import ICustoms from "../../models/product/customs"
import IVat from "../../models/product/vat"
import ITerritoi from "../../models/product/territoi"
import { ITreeNode } from "../../utils/tree/interfaces"
import { getFormDataFromFields, fetchProducts } from "../../core/actions"
import { Languages, Roles } from "../../utils"
import ButtonChange, {
  ButtonChangeSampleOff,
  ButtonChangeSampleOn,
} from "./buttons/com_button-change"
import Loading from "./com_loading"
import { getProductList } from "../../core/reducers/red_products"
import { getSelectedSupplier } from "../../core/reducers/red_user"
import { ITax } from "../../models/bo"

// ------------------------ VIEW ------------------------
interface IMultiModifModalProps {
  state: ProductFormState
  actions: ProductFormActions
  formId: "multiModif"
  translations: ITranslations["fieldErrors"] &
    ITranslations["multiModif"] &
    ITranslations["productForm"] &
    ITranslations["productList"]
  selectedProducts: IReferenceIds[]
  customs: ICustoms[]
  territoi: ITerritoi[]
  brands: string[]
  vat: IVat[]
  warrantyOptionList: IDropdownOption[]
  taxes: ITax[]
  techPropNodes: ITreeNode[]
  techRawData: ITreeNode[]
  comPropNodes: ITreeNode[]
  comRawData: ITreeNode[]
  activeLang: Languages
  supplier: ISupplierInfo
  role: Roles
  fullProductList: IRawSupplierReference[]
  refreshProductList: () => Promise<boolean>
}

const MultiModifModal = ({
  state,
  actions,
  formId,
  activeLang,
  techPropNodes,
  comPropNodes,
  techRawData,
  comRawData,
  translations,
  selectedProducts,
  territoi,
  brands,
  customs,
  vat,
  warrantyOptionList,
  taxes,
  role,
  supplier,
  fullProductList,
  refreshProductList,
}: IMultiModifModalProps) => {
  const [loadCaratech, setLoadCaratech] = React.useState(false)
  const [isVariant, setIsVariant] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    actions._resetFormId(formId)
  }

  const disabled = selectedProducts.length < 2
  const loading = state.loading > 0
  const {
    isInitialized,
    standardFields,
    techFields,
    techFieldsRawData,
    deletedFileNames,
    product,
  } = state.dataByFormId[formId]

  React.useEffect(() => {
    handleChangeFamilyTech()
  }, [stringifyTree(standardFields.techNode.value)])

  React.useEffect(() => {
    if (!open) {
      return
    }
    actions._startLoading()
    if (!disabled) {
      // filter selected products on topRef null
      const droopyIds = selectedProducts
        .filter((p) => !p.topRef)
        .map((p) => p.droopyId)

      $getMultiModifProductList(droopyIds, supplier.supplierName).then(
        (products) => {
          const mergedProduct = mergeSelectedFullProducts(products)
          let willBeVariant = false
          products.forEach((p) => {
            willBeVariant =
              willBeVariant ||
              !!fullProductList.find(
                (e) => e.isTopRef && e.supplierRef === p?.topRef
              )
          })
          setIsVariant(willBeVariant)

          actions._storeProduct(formId, {
            product: mergedProduct,
            comRawData,
            techRawData,
          })
        }
      )
    } else {
      actions._stopLoading()
    }
  }, [stringify(open)])

  const handleChangeFamilyTech = () => {
    setLoadCaratech(true)
    $getFieldsByFamilyTech(standardFields.techNode.value.id)
      .then((response) => {
        const techF = response.map((rawData) =>
          createFormFieldByCaratech(rawData)
        )
        if (standardFields.techNode.value.code === product?.techNode) {
          actions._storeTechFields(formId, {
            fields: techF,
            rawData: response,
            techValues: product.techFields,
          })
        } else {
          actions._storeTechFields(formId, {
            fields: techF,
            rawData: response,
            modified: false,
          })
        }
      })
      .finally(() => setLoadCaratech(false))
  }

  const handleChangeField = ({
    code,
    fieldType,
    lock,
    fieldValue,
    overrideDefaultValue = false,
  }: IHandleChangeProps) => {
    const field =
      fieldType === FieldType.Standard ? standardFields[code] : techFields[code]
    if (
      field !== undefined &&
      ((fieldValue !== undefined && fieldValue !== field.value) ||
        lock !== field.lock)
    ) {
      const errorMessage = checkFieldValidity({
        field: { ...field, value: fieldValue || field.value },
        translations,
      })
      actions._onChangeField(formId, {
        code,
        fieldValue: fieldValue || field.value,
        fieldType,
        lock,
        errorMessage,
        overrideDefaultValue,
      })
    }
  }

  const handleFieldLockClick = (event: any, fieldType: FieldType) => {
    event.preventDefault()
  }

  const handleFieldModifyBtnClick = (event: any, fieldType: FieldType) => {
    event.preventDefault()
    if (fieldType === FieldType.Tech && standardFields.techNode.modified) {
      toastr.warning(
        translations.cantChangeTechFieldHeader,
        translations.cantChangeTechFieldMessage
      )
      return
    }
    const code = event.currentTarget.name
    const modified = !(event.currentTarget.value === "true")
    actions._changeModifiedState({ code, modified })
    if (
      !modified &&
      product &&
      comRawData.length > 0 &&
      techRawData.length > 0
    ) {
      actions._storeProduct(formId, { product, comRawData, techRawData })
    }
  }

  const handleSubmit = () => {
    if (
      !window.confirm(
        `Êtes vous sur de vouloir modifier ${selectedProducts.length} produits?`
      )
    ) {
      return
    }

    actions._startLoading()

    const droopyIds = selectedProducts
      .filter((p) => !p.topRef)
      .map((o) => o.droopyId)
    const declinedDroopyIds = selectedProducts
      .filter((p) => p.topRef)
      .map((o) => o.droopyId)

    const caraTechOptions = Object.keys(techFields).reduce((acc, k) => {
      const o = techFields[k].options
      return o.length > 0 ? { ...acc, [k]: o } : acc
    }, {} as { [key: string]: IDropdownOption<any>[] })

    const standardMod = Object.values(standardFields).reduce((acc, field) => {
      if (field?.modified) {
        return { ...acc, [field.code]: field }
      }
      return acc
    }, {} as { [fieldName: string]: IFieldState<any> })

    const techMod = Object.values(techFields).reduce((acc, field) => {
      if (field?.modified) {
        return { ...acc, [field.code]: field }
      }
      return acc
    }, {} as { [fieldName: string]: IFieldState<any> })

    $sendMultiModifInformations(
      droopyIds,
      declinedDroopyIds,
      getFormDataFromFields(
        // @ts-ignore
        standardMod,
        techMod,
        techFieldsRawData,
        caraTechOptions,
        role,
        deletedFileNames
      ),
      translations
    )
      .then((res) => res.json())
      .then(() => {
        handleClose()
        refreshProductList()
      })
      .finally(() => actions._stopLoading())
  }

  const placeholder = (source: string) => () =>
    console.log("placeholder" + source)

  const onFieldLooseFocus = placeholder("onFieldLooseFocus")

  return (
    <Modal
      trigger={
        <Button
          className="element button colored-button"
          disabled={disabled}
          onClick={handleOpen}
        >
          {translations.modifProductButtonLabel}
        </Button>
      }
      onClose={handleClose}
      open={open}
      size={"large"}
      dimmer="inverted"
      className="multi-modal"
    >
      {loading && <Loading />}
      <Modal.Header>{translations.header}</Modal.Header>
      <Modal.Content scrolling>
        {/* <Message
          info
          icon="warning sign"
          header={translations.messageHeader}
          content={
            <div>
              <Divider />
              {translations.messageLineOne}
              <div className="multi-modif-inline-example">
                <div>
                  <div className="example-text">
                    {translations.exampleTextOne}
                  </div>
                  <div className="example-button">
                    <ButtonChangeSampleOff />
                    <Icon name="arrow right" />
                    <ButtonChangeSampleOn />
                  </div>
                </div>
              </div>
              {translations.messageLineTwo}
              <div className="multi-modif-inline-example">
                <div>
                  <div className="example-text">
                    {translations.exampleTextTwo}
                  </div>
                  <div className="example-button">
                    <ButtonChangeSampleOn />
                    <Icon name="arrow right" />
                    <ButtonChangeSampleOff />
                  </div>
                </div>
              </div>
            </div>
          }
        /> */}
        <CommonFields
          supplier={supplier}
          lockable={true}
          tabIndex={1}
          isVariant={isVariant}
          isMarketLeader={role === Roles.MARKET_LEADER}
          comNodeDisabledMessage={translations.comNodeDisabledVariant}
          techNodeDisabledMessage={translations.techNodeDisabledVariant}
          techFieldsIsLoading={loadCaratech}
          vatOptionList={vat}
          warrantyOptionList={warrantyOptionList}
          taxesList={taxes}
          customsOptionList={customs}
          territoiOptionList={territoi}
          brandOptionList={brands}
          handleChangeField={handleChangeField}
          onFieldLockClick={handleFieldLockClick}
          handleFieldModifyBtnClick={handleFieldModifyBtnClick}
          onFieldLooseFocus={onFieldLooseFocus}
          {...{
            standardFields,
            techFields,
            techRawData: techFieldsRawData,
            comPropNodes,
            techPropNodes,
            translations,
            activeLang,
            formId,
          }}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          inverted
          color="red"
          icon="remove"
          labelPosition="right"
          content={translations.close}
          onClick={handleClose}
        />
        <Button
          inverted
          color="green"
          icon="checkmark"
          labelPosition="right"
          content={translations.validate}
          onClick={handleSubmit}
        />
      </Modal.Actions>
    </Modal>
  )
}

// ------------------------ INITIALIZER ------------------------

const mapStateToProps = (state: DropitState, props: any) => {
  return {
    formId: permanentFormIds.MULTI_MODIF,
    activeLang: getActiveLang(state),
    state: state.productForm as ProductFormState,
    selectedProducts: props.selectedRefs as IReferenceIds[],
    vat: state.miscellaneous.vat,
    warrantyOptionList: state.miscellaneous.warranty,
    taxes: state.miscellaneous.taxes,
    customs: state.miscellaneous.customs,
    territoi: state.miscellaneous.territoi,
    brands: state.miscellaneous.brands,
    techPropNodes: [state.miscellaneous.techTree.data],
    techRawData: state.miscellaneous.techTree.rawData,
    comPropNodes: [state.miscellaneous.comTree.data],
    comRawData: state.miscellaneous.comTree.rawData,
    role: getUserRole(state),
    supplier: getUserSelectedSupplier(state),
    translations: getTranslationsByScopes(state, [
      "multiModif",
      "productForm",
      "productList",
      "fieldErrors",
    ]) as ITranslations["fieldErrors"] &
      ITranslations["multiModif"] &
      ITranslations["productForm"] &
      ITranslations["productList"],
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  refreshProductList: () => dispatch(fetchProducts()),
  actions: getActions(dispatch),
})

export default connect<any, any, any, DropitState>(
  mapStateToProps,
  mapDispatchToProps
)(MultiModifModal)
